import { GroupRowType } from "helpers/models";
import { Content } from "pdfmake/interfaces";
import formatNumber from "report/common/format-number";
import layout from "report/common/layout";

const DISTANCE_FIELD = "__lineDistanceMeters";

const parseLineLengthToMeters = (lineLength?: string): number | undefined => {
  if (!lineLength) return undefined;

  const numericMatch = lineLength.replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  if (!numericMatch) return undefined;

  const value = Number(numericMatch[0]);
  if (!Number.isFinite(value)) return undefined;

  const lower = lineLength.toLowerCase();
  const isKm =
    /\bkm\b/.test(lower) ||
    lineLength.includes("千米") ||
    lineLength.includes("公里");

  return isKm ? value * 1000 : value;
};

/**
 * Line-analysis variant of table-column.
 * Differences from the common version:
 *  - No currency column
 *  - Share column header uses "Distance" label instead of "Number of Locations"
 */
const get = (
  t: any,
  data: GroupRowType[],
  aggregationUnits: string[],
  text: string = t("fathom.Return Period"),
  unit: string = "numberOfLocations",
  distanceUnit: "m" | "km" = "m",
  lineLength?: string
): Content => {
  const toDisplayDistance = (meters: number) =>
    distanceUnit === "km" ? meters / 1000 : meters;

  const formatDistance = (meters: number): string => {
    const value = toDisplayDistance(meters);
    return value.toFixed(3);
  };

  const getDistanceHeader = () =>
    `${t("drawShape.length")} (${distanceUnit === "km" ? t("drawShape.km") : t("drawShape.m")})`;

  const aggHeader = aggregationUnits.map((agg) => ({
    text:
      agg === "numberOfLocations" ? getDistanceHeader() : t(`locations.${agg}`),
    alignment: "right"
  }));

  const header = [
    { text: text, bold: true, colSpan: 2 },
    {},
    ...aggHeader,
    {
      text: t("report.Share"),
      alignment: "right"
    },
    {}
  ];

  const widths = header.map((_, i) => {
    if (i === 0) return 15; // Color box
    if (i === 1) return 100; // Label
    if (i === header.length - 1) return 150; // Percentage bar
    return "*";
  });

  const totalDistanceMeters = data.reduce(
    (sum, item) => sum + Number((item as any)[DISTANCE_FIELD] || 0),
    0
  );
  const totalCount = data.reduce(
    (sum, item) => sum + Number(item.count || 0),
    0
  );
  const lineLengthMetersBase =
    parseLineLengthToMeters(lineLength) ?? totalDistanceMeters;
  const totalAgg = data.reduce(
    (sum, item) => sum + Number(item[unit as keyof GroupRowType] || 0),
    0
  );

  const row = data.map((e) => {
    const rowCount = Number(e.count || 0);
    const percentByCount = totalCount > 0 ? (rowCount / totalCount) * 100 : 0;
    const rowDistanceByCount = lineLengthMetersBase * (percentByCount / 100);
    const percentAgg =
      totalAgg > 0
        ? (Number(e[unit as keyof GroupRowType] || 0) / totalAgg) * 100
        : 0;
    const percent = unit === "numberOfLocations" ? percentByCount : percentAgg;

    const aggColumn = aggregationUnits.map((agg) => {
      if (agg === "numberOfLocations") {
        return {
          text: formatDistance(rowDistanceByCount),
          alignment: "right"
        };
      }
      const v = e[agg as keyof GroupRowType];
      if (v === null || v === undefined) {
        return { text: " - ", alignment: "right" };
      }
      const fn = formatNumber(v as number);
      return { text: fn, alignment: "right" };
    });

    return [
      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 0,
            w: 12,
            h: 12,
            lineColor: "#E4E4E4",
            lineWidth: 1,
            color: e.color || "#CCCCCC"
          }
        ]
      },

      t("legend." + e.label),

      ...aggColumn,

      { text: percent.toFixed(2) + "%", alignment: "right" },

      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 3,
            w: percent * 1.5,
            h: 4,
            color: e.color || "#CCCCCC"
          }
        ],
        alignment: "right"
      }
    ];
  });

  const summary = data.reduce(
    (acc, curr) => {
      aggregationUnits.forEach((key) => {
        if (key === "numberOfLocations") {
          acc[key] =
            (acc[key] || 0) + Number((curr as any)[DISTANCE_FIELD] || 0);
          return;
        }
        if (typeof curr[key as keyof GroupRowType] === "number") {
          const k = key as keyof typeof acc;
          acc[k] = (acc[k] || 0) + (curr[key as keyof GroupRowType] as number);
        }
      });
      return acc;
    },
    {} as { [key: string]: number }
  );

  const footerAggValues = aggregationUnits.map((agg) => ({
    text:
      summary[agg] !== undefined && summary[agg] !== null
        ? agg === "numberOfLocations"
          ? formatDistance(lineLengthMetersBase)
          : formatNumber(summary[agg]).toString()
        : " - ",
    alignment: "right"
  }));

  const footer = [
    { text: t("report.Total"), colSpan: 2 },
    {},
    ...footerAggValues,
    { text: "100%", alignment: "right" },
    {
      canvas: [
        {
          type: "rect",
          x: 0,
          y: 3,
          w: 150,
          h: 4,
          color: "black"
        }
      ],
      alignment: "right"
    }
  ];

  return {
    table: {
      headerRows: 1,
      widths,
      body: [header, ...row, footer]
    },
    layout: layout(0, true, 6),

    unbreakable: true
  };
};

export default get;
